import React, { useState } from 'react';
import { Loader2, Lock, RotateCcw, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import api from '@/lib/api';

const money = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const br = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : '—');
const num = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const errMsg = (e, d) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : d);
const LIQ_MSG = 'Parcela já liquidada — utilize estorno antes de alterar.';

function InstallmentEditor({ row, base, onCancel, onSaved }) {
  const [f, setF] = useState({
    amount: row.valor_bruto ?? row.valor, due_date: row.vencimento || '', discount_type: row.desconto_tipo || 'none',
    discount_value: row.desconto_valor || 0, abatimento: row.abatimento || 0, notes: row.observacao || '',
  });
  const [busy, setBusy] = useState(false);
  const s = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const amt = num(f.amount) || 0; const dv = num(f.discount_value) || 0;
  const desc = f.discount_type === 'percent' ? (amt * dv) / 100 : f.discount_type === 'fixed' ? dv : 0;
  const final = Math.max(amt - desc - (num(f.abatimento) || 0), 0);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`${base}/installment/${row.id}`, {
        amount: amt, due_date: f.due_date, discount_type: f.discount_type, discount_value: dv,
        abatimento: num(f.abatimento) || 0, notes: f.notes || null,
      });
      toast.success(`Parcela ${row.referencia} personalizada`);
      onSaved();
    } catch (e) { toast.error(errMsg(e, 'Falha ao salvar a parcela')); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-violet-200 bg-violet-50/60 p-3" data-testid="parcela-editor">
      <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <b data-testid="parcela-editor-ref">Parcela {row.referencia}</b>
        <span>Valor atual: <b>{money(row.valor)}</b></span>
        <span>Vencimento atual: <b>{br(row.vencimento)}</b></span>
      </div>
      {row.boleto_remessa && <p className="text-xs text-amber-700">Boleto/remessa registrado: o vencimento só muda após reemitir o boleto.</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div><Label className="text-xs font-semibold">Valor (R$)</Label><Input inputMode="decimal" value={f.amount} onChange={s('amount')} data-testid="parcela-valor" /></div>
        <div><Label className="text-xs font-semibold">Vencimento</Label><Input type="date" value={f.due_date} onChange={s('due_date')} disabled={row.boleto_remessa} data-testid="parcela-vencimento" /></div>
        <div><Label className="text-xs font-semibold">Desconto · tipo</Label>
          <select value={f.discount_type} onChange={s('discount_type')} data-testid="parcela-desc-tipo" className="mt-1 h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="none">Sem desconto</option><option value="percent">Percentual (%)</option><option value="fixed">Valor fixo (R$)</option>
          </select></div>
        <div><Label className="text-xs font-semibold">{f.discount_type === 'percent' ? 'Desconto (%)' : 'Desconto (R$)'}</Label><Input inputMode="decimal" value={f.discount_value} disabled={f.discount_type === 'none'} onChange={s('discount_value')} data-testid="parcela-desc-valor" /></div>
        <div><Label className="text-xs font-semibold">Abatimento (R$)</Label><Input inputMode="decimal" value={f.abatimento} onChange={s('abatimento')} data-testid="parcela-abatimento" /></div>
        <div className="col-span-2 sm:col-span-3"><Label className="text-xs font-semibold">Observação</Label><Input value={f.notes} onChange={s('notes')} data-testid="parcela-obs" /></div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">Novo valor da parcela: <b data-testid="parcela-novo-valor">{money(final)}</b> · só esta parcela muda; o plano-base não é alterado.</p>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel} disabled={busy} data-testid="parcela-cancelar">Cancelar</Button>
          <Button onClick={save} disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="parcela-salvar">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar parcela'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export function ParcelasPlanoTable({ rows, base, onChanged }) {
  const [editId, setEditId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const editing = rows.find((r) => r.id === editId);
  const restore = async (r) => {
    if (!window.confirm(`Restaurar a parcela ${r.referencia} aos valores e regras do plano-base?`)) return;
    setBusyId(r.id);
    try { await api.post(`${base}/installment/${r.id}/restore`); toast.success(`Parcela ${r.referencia} restaurada ao plano`); onChanged(); }
    catch (e) { toast.error(errMsg(e, 'Falha ao restaurar')); } finally { setBusyId(null); }
  };
  return (
    <div className="space-y-2" data-testid="parcelas-plano">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Parcelas do plano (títulos reais)</p>
      {editing && <InstallmentEditor key={editing.id} row={editing} base={base} onCancel={() => setEditId(null)} onSaved={() => { setEditId(null); onChanged(); }} />}
      <div className="max-h-64 overflow-auto rounded-lg border border-slate-200">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr><th className="px-2 py-1 text-left">Parcela</th><th className="text-left">Status</th><th className="text-left">Vencimento</th><th className="text-right">Valor</th><th className="px-2 text-left">Origem</th><th className="px-2 text-right">Ação</th></tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className={`border-t border-slate-100 ${r.id === editId ? 'bg-violet-50' : ''}`} data-testid={`parcela-row-${r.parcela || r.id}`}>
                <td className="px-2 py-1 font-semibold">{r.referencia || r.parcela || '—'}</td>
                <td>{r.status}</td>
                <td>{br(r.vencimento)}</td>
                <td className="text-right">{money(r.valor)}</td>
                <td className="px-2">
                  {r.personalizada
                    ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800" title={r.observacao || ''} data-testid={`parcela-origem-${r.parcela}`}>Personalizada</span>
                    : <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600" data-testid={`parcela-origem-${r.parcela}`}>Plano</span>}
                </td>
                <td className="whitespace-nowrap px-2 py-1 text-right">
                  {r.liquidada ? (
                    <button type="button" className="inline-flex items-center gap-1 text-slate-400" onClick={() => toast.info(LIQ_MSG)} title={LIQ_MSG} data-testid={`parcela-bloqueada-${r.parcela}`}>
                      <Lock className="h-3 w-3" /> Liquidada
                    </button>
                  ) : (
                    <>
                      <button type="button" className="inline-flex items-center gap-1 font-semibold text-violet-700 hover:underline" onClick={() => setEditId(r.id)} data-testid={`parcela-editar-${r.parcela}`}>
                        <Pencil className="h-3 w-3" /> Editar
                      </button>
                      {r.personalizada && (
                        <button type="button" disabled={busyId === r.id} className="ml-3 inline-flex items-center gap-1 text-slate-600 hover:underline" onClick={() => restore(r)} data-testid={`parcela-restaurar-${r.parcela}`}>
                          <RotateCcw className="h-3 w-3" /> Restaurar ao plano
                        </button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="py-3 text-center text-slate-400">Nenhum título gerado para este plano.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
